import React from 'react';
import { View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity, Dimensions, Linking, Alert, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width } = Dimensions.get('window');

const FeedbackScreen = () => {
    const { colors } = useTheme();
    const isMobile = width < 768;

    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        rating: 0,
        feedbackType: '',
        message: '',
    });

    const feedbackTypes = [
        { id: 'suggestion', label: 'Suggestion', icon: 'bulb' },
        { id: 'praise', label: 'Praise', icon: 'heart' },
        { id: 'complaint', label: 'Complaint', icon: 'warning' },
        { id: 'other', label: 'Other', icon: 'chatbox' },
    ];

    const handleSubmit = () => {
        const mailtoUrl = `mailto:shubham@modernmunshiji.com?subject=Feedback: ${formData.feedbackType}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\nRating: ${formData.rating}/5 stars\nType: ${formData.feedbackType}\n\n${formData.message}`)}`;
        Linking.openURL(mailtoUrl);
    };

    const styles = StyleSheet.create({
        container: { flex: 1, backgroundColor: colors.background },
        scrollView: { flex: 1 },
        hero: {
            backgroundColor: '#1a1a4e',
            paddingVertical: 60,
            paddingHorizontal: 20,
            alignItems: 'center',
        },
        heroTitle: {
            fontSize: isMobile ? 28 : 40,
            fontWeight: '800',
            color: '#c9a44e',
            textAlign: 'center',
            marginBottom: 12,
        },
        heroSubtitle: {
            fontSize: 16,
            color: 'rgba(255,255,255,0.8)',
            textAlign: 'center',
            maxWidth: 600,
        },
        content: {
            padding: 20,
            maxWidth: 700,
            alignSelf: 'center',
            width: '100%',
        },
        formCard: {
            backgroundColor: colors.surface,
            borderRadius: 16,
            padding: 30,
            borderWidth: 1,
            borderColor: colors.border,
        },
        inputGroup: {
            marginBottom: 20,
        },
        label: {
            fontSize: 14,
            fontWeight: '600',
            color: colors.text,
            marginBottom: 10,
        },
        input: {
            backgroundColor: colors.background,
            borderWidth: 1,
            borderColor: colors.border,
            borderRadius: 8,
            paddingHorizontal: 16,
            paddingVertical: 14,
            fontSize: 15,
            color: colors.text,
        },
        ratingRow: {
            flexDirection: 'row',
            gap: 8,
        },
        star: {
            padding: 4,
        },
        typeRow: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 12,
        },
        typeButton: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 12,
            paddingHorizontal: 16,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: colors.border,
            backgroundColor: colors.background,
            gap: 8,
        },
        typeButtonSelected: {
            backgroundColor: '#1a1a4e',
            borderColor: '#1a1a4e',
        },
        typeButtonText: {
            fontSize: 14,
            color: colors.text,
        },
        typeButtonTextSelected: {
            color: '#c9a44e',
        },
        textArea: {
            height: 140,
            textAlignVertical: 'top',
        },
        submitButton: {
            backgroundColor: '#c9a44e',
            paddingVertical: 16,
            borderRadius: 8,
            alignItems: 'center',
            marginTop: 10,
        },
        submitButtonText: {
            color: '#1a1a4e',
            fontWeight: '700',
            fontSize: 16,
        },
    });

    return (
        <View style={styles.container}>
            <Header />
            <ScrollView style={styles.scrollView}>
                <View style={styles.hero}>
                    <Text style={styles.heroTitle}>Share Your Feedback</Text>
                    <Text style={styles.heroSubtitle}>
                        We value your opinion! Help us improve Modern Munshiji with your valuable feedback.
                    </Text>
                </View>

                <View style={styles.content}>
                    <View style={styles.formCard}>
                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Your Name</Text>
                            <TextInput
                                style={styles.input}
                                placeholder="Enter your name"
                                placeholderTextColor={colors.textSecondary}
                                value={formData.name}
                                onChangeText={(text) => setFormData({ ...formData, name: text })}
                            />
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Email Address</Text>
                            <TextInput
                                style={styles.input}
                                placeholder="Enter your email"
                                placeholderTextColor={colors.textSecondary}
                                keyboardType="email-address"
                                value={formData.email}
                                onChangeText={(text) => setFormData({ ...formData, email: text })}
                            />
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>How would you rate your experience?</Text>
                            <View style={styles.ratingRow}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <TouchableOpacity
                                        key={star}
                                        style={styles.star}
                                        onPress={() => setFormData({ ...formData, rating: star })}
                                    >
                                        <Ionicons
                                            name={star <= formData.rating ? 'star' : 'star-outline'}
                                            size={32}
                                            color="#c9a44e"
                                        />
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>What type of feedback is this?</Text>
                            <View style={styles.typeRow}>
                                {feedbackTypes.map((type) => (
                                    <TouchableOpacity
                                        key={type.id}
                                        style={[
                                            styles.typeButton,
                                            formData.feedbackType === type.id && styles.typeButtonSelected,
                                        ]}
                                        onPress={() => setFormData({ ...formData, feedbackType: type.id })}
                                    >
                                        <Ionicons
                                            name={type.icon}
                                            size={18}
                                            color={formData.feedbackType === type.id ? '#c9a44e' : colors.textSecondary}
                                        />
                                        <Text
                                            style={[
                                                styles.typeButtonText,
                                                formData.feedbackType === type.id && styles.typeButtonTextSelected,
                                            ]}
                                        >
                                            {type.label}
                                        </Text>
                                    </TouchableOpacity>
                                ))}
                            </View>
                        </View>

                        <View style={styles.inputGroup}>
                            <Text style={styles.label}>Your Feedback</Text>
                            <TextInput
                                style={[styles.input, styles.textArea]}
                                placeholder="Tell us what you think..."
                                placeholderTextColor={colors.textSecondary}
                                multiline
                                value={formData.message}
                                onChangeText={(text) => setFormData({ ...formData, message: text })}
                            />
                        </View>

                        <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
                            <Text style={styles.submitButtonText}>Submit Feedback</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                <Footer />
            </ScrollView>
        </View>
    );
};

export default FeedbackScreen;
